import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, registerUser } from "../api/client";
import { BRAND } from "../brand";

const HERO_IMAGES = [
  "/images/heroes/mm/hero-1.webp",
  "/images/heroes/mm/hero-2.webp",
  "/images/heroes/mm/hero-3.webp",
  "/images/heroes/mm/hero-4.webp",
  "/images/heroes/mm/hero-5.webp",
  "/images/heroes/mm/hero-6.webp",
  "/images/heroes/mm/hero-7.webp",
];

function randomHeroImage(): string {
  return HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)] as string;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [confirmedAge, setConfirmedAge] = useState(false);
  const [consentHealthProcessing, setConsentHealthProcessing] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [heroImage] = useState(randomHeroImage);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");

    if (!agreedToTerms) return setError("You must agree to the Terms & Conditions and Privacy Policy to create an account.");
    if (!confirmedAge) return setError("You must confirm you are 16 or older.");
    if (!consentHealthProcessing) return setError("Health data processing consent is required to use the service.");

    setBusy(true);
    try {
      await registerUser({
        email: email.trim(),
        displayName: displayName.trim() || undefined,
        agreedToTerms,
        confirmedAge,
        consentHealthProcessing,
      });
      navigate("/auth/check-email");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "We couldn't create your account. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-[70vh] items-center justify-center overflow-hidden rounded-xl">
      <div className="absolute inset-0 hidden lg:block">
        <img src={heroImage} alt={`${BRAND.name} training`} className="h-full w-full object-cover" data-testid="auth-hero-image" />
        <div className="absolute inset-0 bg-black/40" />
      </div>
      <div className="relative flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-8 rounded-xl border border-white/10 bg-gray-900/70 p-8 shadow-2xl backdrop-blur-lg dark:bg-gray-900/80">
          <div className="text-center">
            <div className="mb-6 flex items-center justify-center gap-2.5">
              <img src={BRAND.logoPath} alt={BRAND.name} className="h-9 w-9" />
              <span className="text-xl font-semibold tracking-tight text-white">{BRAND.name}</span>
            </div>
            <h1 className="text-2xl font-semibold text-white" data-testid="register-page-title">Create your account</h1>
            <p className="mt-1.5 text-sm text-gray-300">Build your engine. Strength that moves with your miles.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form-submit">
            {error && <p className="rounded-lg border border-amber-700/50 bg-amber-900/30 px-3 py-2.5 text-sm text-amber-400" data-testid="register-error-message">{error}</p>}
            <div>
              <label htmlFor="register-email" className="mb-1.5 block text-sm font-medium text-gray-200">Email</label>
              <input id="register-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="you@example.com" data-testid="register-email-input" />
            </div>
            <div>
              <label htmlFor="register-display-name" className="mb-1.5 block text-sm font-medium text-gray-200">Display name</label>
              <input id="register-display-name" type="text" value={displayName} onChange={(event) => setDisplayName(event.target.value)} className="w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Your name" data-testid="register-display-name-input" />
            </div>
            <label className="flex items-start gap-3 text-sm text-gray-300" data-testid="register-terms-label">
              <input type="checkbox" checked={agreedToTerms} onChange={(event) => setAgreedToTerms(event.target.checked)} className="mt-1" data-testid="register-terms-input" />
              <span>I agree to the Terms &amp; Conditions and Privacy Policy.</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-gray-300" data-testid="register-age-label">
              <input type="checkbox" checked={confirmedAge} onChange={(event) => setConfirmedAge(event.target.checked)} className="mt-1" data-testid="register-age-input" />
              <span>I confirm I am 16 years of age or older.</span>
            </label>
            <label className="flex items-start gap-3 text-sm text-gray-300" data-testid="register-health-label">
              <input type="checkbox" checked={consentHealthProcessing} onChange={(event) => setConsentHealthProcessing(event.target.checked)} className="mt-1" data-testid="register-health-consent-input" />
              <span>I consent to {BRAND.name} processing my health and fitness data.</span>
            </label>
            <button type="submit" disabled={busy} className="w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition-all hover:bg-emerald-700 disabled:opacity-60" data-testid="register-submit-button">
              {busy ? "Creating…" : "Create Account"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-300">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-emerald-400 hover:text-emerald-300" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
